import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import logoApp from '../assets/logo-app.png';
import './Login.css';

const Login = () => {
  const { showSuccess, showError } = useNotification();
  const supportEmail = 'suporte@qadoro.net';
  const butterflies = [0.73428, 0.8213, 0.46092, 0.05981, 0.20235, 0.91763, 0.3448, 0.27659, 0.08087, 0.6667, 0.39124, 0.15907, 0.54112, 0.1132, 0.94771, 0.61245, 0.28812, 0.87219, 0.02643, 0.71318];
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { login } = useAuth();

  // Validação de e-mail
  const validateEmail = (email) => {
    // Regex mais robusta para validação de e-mail
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!email) {
      return 'E-mail é obrigatório';
    }
    
    // Verificar se não há espaços em branco
    if (/\s/.test(email)) {
      return 'E-mail não pode conter espaços';
    }
    
    // Verificar comprimento máximo (padrão RFC 5321)
    if (email.length > 254) {
      return 'E-mail muito longo (máximo 254 caracteres)';
    }
    
    // Verificar formato
    if (!emailRegex.test(email)) {
      return 'Formato de e-mail inválido (ex.: usuario@dominio.com)';
    }
    
    // Verificar se não começa ou termina com ponto
    const localPart = email.split('@')[0];
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      return 'E-mail não pode começar ou terminar com ponto';
    }
    
    // Verificar pontos consecutivos
    if (localPart.includes('..')) {
      return 'E-mail não pode ter pontos consecutivos';
    }
    
    return '';
  };

  // Validação de senha
  const validatePassword = (password) => {
    if (!password) {
      return 'Senha é obrigatória';
    }
    if (password.length < 8) {
      return 'Senha deve ter no mínimo 8 caracteres';
    }
    if (password.length > 20) {
      return 'Senha deve ter no máximo 20 caracteres';
    }
    
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (!hasUpperCase) {
      return 'Senha deve conter pelo menos uma letra maiúscula';
    }
    if (!hasLowerCase) {
      return 'Senha deve conter pelo menos uma letra minúscula';
    }
    if (!hasNumber) {
      return 'Senha deve conter pelo menos um número';
    }
    if (!hasSpecialChar) {
      return 'Senha deve conter pelo menos um caractere especial';
    }
    
    return '';
  };

  // Verificar se o formulário é válido
  const isFormValid = () => {
    return email && password && !emailError && !passwordError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validar campos antes de enviar
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);
    
    setEmailError(emailValidation);
    setPasswordError(passwordValidation);
    
    if (emailValidation || passwordValidation) {
      return;
    }
    
    setLoading(true);

    const result = await login(email, password);
    setPassword('');
    setPasswordError('');
    
    if (!result.success) {
      setError(result.error);
      showError(result.error || 'Erro ao fazer login');
    } else {
      showSuccess('Login realizado com sucesso!');
    }
    
    setLoading(false);
  };

  // Handlers para validação em tempo real
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handlePasswordChange = (e) => {
    const value = String(e.target.value || '').slice(0, 20);
    setPassword(value);
    setPasswordError(validatePassword(value));
  };

  const passwordChangeMailto = `mailto:${supportEmail}?subject=${encodeURIComponent('Solicitação de alteração de senha')}&body=${encodeURIComponent(`Olá, preciso alterar minha senha de acesso.\n\nE-mail da conta: ${email || '[informe seu e-mail]'}\n\nObrigado(a).`)}`;

  return (
    <div className="login-container">
      <div className="login-scene" aria-hidden="true">
        {butterflies.map((seed, idx) => (
          <div key={idx} className="login-butterfly" style={{ '--random': seed }} />
        ))}
      </div>
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-title">
            <img className="login-logo" src={logoApp} alt="QA'doro" />
          </h1>
          <p>Sistema de Gerenciamento de Testes</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={handleEmailChange}
              required
              placeholder="usuario@dominio.com"
              className={emailError ? 'error' : ''}
            />
            {emailError && <div className="field-error">{emailError}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha:</label>
            <input
              type="password"
              id="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={handlePasswordChange}
              maxLength={20}
              required
              placeholder="Sua senha (8-20 caracteres)"
              className={passwordError ? 'error' : ''}
            />
            {passwordError && <div className="field-error">{passwordError}</div>}
          </div>

          {error && <div className="error-message">{error}</div>}

          <button 
            type="submit" 
            disabled={loading || !isFormValid()} 
            className="login-button"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

        </form>
        <div className="register-section">
          <p>
            Não tem uma conta? 
            <Link to="/register" className="register-link"> Criar conta</Link>
          </p>
          <a
            href={passwordChangeMailto}
            className="forgot-password-link"
          >
            Esqueceu a senha?
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
